import type { CustomAttributeData } from '@risksmart-app/domain/src/types/common.types';
import type { ConsequenceType } from '@risksmart-app/domain/src/types/consts/consequence-type';
import type { CostType } from '@risksmart-app/domain/src/types/consts/cost-type';
import type { InsertConsequenceMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from 'src/utils/trpc';

type InsertConsequenceTRPCInput = {
  ParentIssueId: string;
  Title: string;
  Description?: string | null;
  Criticality?: number | null;
  CostType: CostType;
  CostValue: number;
  Type?: ConsequenceType | null;
  CustomAttributeData?: CustomAttributeData | null;
};

/**
 * Maps TRPC mutation response to match the GraphQL mutation structure
 */
const mapTrpcResponseToGraphQL = (trpcData: {
  Id: string;
}): InsertConsequenceMutation => {
  return {
    insert_consequence_one: {
      __typename: 'consequence',
      Id: trpcData.Id,
    },
  };
};

export const useInsertConsequenceTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    ...trpc.frontend.consequence.insert.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.consequence.register.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.consequence.consequenceById.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.consequence.getByParentIssueId.queryKey(),
        });
      },
    }),
  });

  return {
    insertConsequence: async (
      variables: InsertConsequenceTRPCInput
    ): Promise<InsertConsequenceMutation> => {
      const result = await mutation.mutateAsync(variables);

      return mapTrpcResponseToGraphQL(result);
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
